'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import type { MediaCard } from '../utils/search-all-media-cards';

/**
 * 실물이 없는 카드의 자리 — 옅은 바탕에 제목(굵게)과 본문을 큰 글자로 편다. 넘치면 말줄임.
 * 위쪽은 카드 머리(얼굴·이름)가 얹히는 자리라 비워 둔다(`topInset`).
 *
 * 🚨 **글이 아닌 카드(광고·상품)도 여기로 온다** — 그때는 `card.title` 이 제목이다.
 *   ⛔ `card.post` 만 읽지 마라 — 글 원본이 없는 카드가 **빈 칸**이 된다(실측: 폴더의 상품 카드가
 *   사진도 글자도 없는 흰 칸으로 섰다).
 */
export function MediaTextFace({ card, topInset = 0, large = false }: { card: MediaCard; topInset?: number; large?: boolean }) {
  const post = card.post ?? {};
  const title = (typeof post.title === 'string' ? post.title.trim() : '') || String(card.title ?? '').trim();
  const content = typeof post.content === 'string' ? post.content.trim() : '';
  const body = content && content !== title ? content : '';

  return (
    <Box
      sx={{
        width: 1,
        height: 1,
        pt: `${topInset + 12}px`,
        px: large ? 4 : 1.75,
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: large ? 'center' : 'flex-start',
        gap: 1,
        overflow: 'hidden',
        color: 'text.primary',
        bgcolor: 'grey.100',
      }}
    >
      {title && (
        <Typography sx={{ fontSize: large ? 22 : 17, fontWeight: 600, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: large ? 4 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', flexShrink: 0 }}>
          {title}
        </Typography>
      )}
      {body && (
        <Typography color="text.secondary" sx={{ fontSize: large ? 16 : 14, lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-word', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: large ? 18 : 12, WebkitBoxOrient: 'vertical' }}>
          {body}
        </Typography>
      )}
    </Box>
  );
}
